import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@cinqo/ui/globals.css'
import './index.css'
import App from './App'
import { loadDictionnaires, invalidateDictionnaires, getDictionnaires } from './lib/dictionnaires'
import { supabase } from './lib/supabase'
import { initSentry, SentryErrorBoundary } from './lib/sentry'
import { ToastProvider } from '@cinqo/ui'

// Sentry — doit etre appele AVANT le premier render
initSentry()

// Build version — injecte a build-time par vite.config.ts (BUILD_STAMP).
// Contient ISO date + short git SHA (CI) ou 'local'. Permet au user de voir en
// console si le JS charge est frais ou du cache stale (vs hardcoded constante).
declare const __BUILD__: string
console.log('[Cinqo Ops] build', __BUILD__)

// Hydrate les dictionnaires (matieres/contenants/transports) depuis la DB.
// Lisibles seulement connecté (RLS) : on les charge dès qu'une session existe,
// à l'ouverture si déjà connecté (INITIAL_SESSION), sinon à la connexion
// (SIGNED_IN). Jamais en anonyme. Non bloquant : les fallback REF_* assurent
// le premier paint en attendant la résolution.
supabase.auth.onAuthStateChange((event, session) => {
  if (!session || (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN')) return
  if (getDictionnaires().matieres.length > 0) return
  invalidateDictionnaires()
  // Hors du callback : supabase-js déconseille d'y attendre un appel Supabase.
  setTimeout(() => { loadDictionnaires().catch(err => console.warn('[dictionnaires] load failed', err)) }, 0)
})

// Auto-reload sur "Failed to fetch dynamically imported module" — typique
// quand un user a un vieux index.html en cache + un nouveau deploy a change
// le hash des chunks lazy. Le import() echoue car l'ancien chunk n'existe
// plus. UN reload suffit pour recuperer le nouveau index.html + nouveaux
// hashes. Garde anti-loop : on ne reload qu'une fois par session via
// sessionStorage.
const RELOAD_FLAG = 'cinqo:autoreloaded:chunk_404'
window.addEventListener('vite:preloadError', () => triggerChunkReload())
window.addEventListener('error', (e) => {
  if (e.message?.includes('Failed to fetch dynamically imported module')) {
    triggerChunkReload()
  }
})
window.addEventListener('unhandledrejection', (e) => {
  const msg = String(e.reason?.message ?? e.reason ?? '')
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Importing a module script failed')) {
    triggerChunkReload()
  }
})
function triggerChunkReload() {
  if (sessionStorage.getItem(RELOAD_FLAG)) {
    console.error('[chunk-reload] deja tente, on n\'auto-reload pas en boucle')
    return
  }
  sessionStorage.setItem(RELOAD_FLAG, '1')
  console.warn('[chunk-reload] vieux bundle detecte (chunk 404) → auto-reload')
  window.location.reload()
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ToastProvider>
      <SentryErrorBoundary
        fallback={({ error }) => (
          <div className="flex items-center justify-center min-h-screen bg-surface p-8">
            <div className="bg-surface-card border border-error/30 rounded-xl p-6 max-w-lg shadow-md">
              <h1 className="text-h3 font-bold text-error mb-2">Erreur inattendue</h1>
              <p className="text-body text-ink-secondary mb-4">
                Une erreur critique est survenue. L&apos;equipe Cinqo a ete notifiee automatiquement.
              </p>
              <pre className="text-caption text-error bg-error-bg rounded-md p-3 overflow-auto max-h-40 mb-4">
                {error?.toString?.() ?? 'Erreur inconnue'}
              </pre>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-data font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              >
                Recharger l&apos;application
              </button>
            </div>
          </div>
        )}
      >
        <App />
      </SentryErrorBoundary>
    </ToastProvider>
  </StrictMode>,
)
